/**
 * InstallPrompt — Batch #14.289 (mobile) : invitation discrète à installer l'app sur l'écran d'accueil.
 * Android/Chrome : capture `beforeinstallprompt` et déclenche l'installation native.
 * iPhone/iPad (Safari) : affiche la marche à suivre (Partager → Sur l'écran d'accueil).
 * Visible seulement sur /admin et /portal, hors mode installé ; « Plus tard » masque 30 jours.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, X } from "lucide-react";

const KEY = "pwa-install-dismissed-at";
const DAYS_30 = 30 * 24 * 3600 * 1000;

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
const isSafariIos = () => isIos() && /safari/i.test(window.navigator.userAgent) && !/crios|fxios|edgios/i.test(window.navigator.userAgent);
const dismissedRecently = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < DAYS_30;

export default function InstallPrompt() {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);
  const inApp = pathname.startsWith("/admin") || pathname.startsWith("/portal");

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isSafariIos()) { setIos(true); setVisible(true); }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!visible || !inApp) return null;
  const portal = pathname.startsWith("/portal");
  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setVisible(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    if (choice?.outcome === "accepted") setVisible(false); else dismiss();
    setDeferred(null);
  };

  return (
    <div className={`fixed inset-x-3 z-50 ${portal ? "bottom-3" : "bottom-[calc(60px+env(safe-area-inset-bottom))] lg:bottom-4"} lg:left-auto lg:right-4 lg:max-w-sm`} data-testid="pwa-install-prompt">
      <div className="bg-slate-900 text-white rounded-2xl shadow-2xl p-3.5 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <img src="/icon-192.png" alt="" className="w-7 h-7 rounded-md" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Installer {portal ? "le portail" : "l'app"} sur votre téléphone</p>
          {ios ? (
            <p className="text-xs text-white/70 mt-1 leading-snug">Touchez <Share className="inline w-3.5 h-3.5 -mt-0.5" /> <strong>Partager</strong> puis <strong>« Sur l'écran d'accueil »</strong> pour y accéder d'un seul geste.</p>
          ) : (
            <p className="text-xs text-white/70 mt-1 leading-snug">Accès direct depuis l'écran d'accueil, plein écran, sans passer par le navigateur.</p>
          )}
          <div className="flex gap-2 mt-2.5">
            {!ios && <button onClick={install} className="inline-flex items-center gap-1.5 bg-white text-slate-900 text-xs font-semibold px-3 py-1.5 rounded-lg active:scale-95 transition" data-testid="pwa-install-btn"><Download className="w-3.5 h-3.5" /> Installer</button>}
            <button onClick={dismiss} className="text-xs text-white/70 px-2 py-1.5 hover:text-white" data-testid="pwa-install-later">Plus tard</button>
          </div>
        </div>
        <button onClick={dismiss} className="text-white/50 hover:text-white p-1 -m-1" aria-label="Fermer" data-testid="pwa-install-close"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
